import React, { useMemo, useState } from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import { ArrowBack as BackIcon, Dashboard as TemplatesIcon } from '@mui/icons-material';
import InlineAlert from '../../../../shared/components/InlineAlert';
import DetailRow from '../../../../shared/components/DetailRow';
import EmptyStateMessage from '../../../../shared/components/EmptyStateMessage';
import FormActionBar from '../../../../shared/components/FormActionBar';
import { EntityListPanel } from '../../../../shared/components/EntityListPanel';
import type { EntityListItemConfig } from '../../../../shared/components/EntityListPanel';
import DatasetConfirmDialog from './DatasetConfirmDialog';
import {
  useDatasetWriteAccess,
  useDeleteEventDatasetTemplate,
  useEventDatasetTemplates,
} from '../../hooks/useEventDatasets';
import { inputLabel } from '../../utils/datasetMappingUtils';
import type { EventDatasetDefinition, EventDatasetTemplate } from '../../../../types/backend';

// Global templates are authored outside this screen, so only a customer's own can be deleted here.
const GROUPS = {
  global: { key: 'global', label: 'Global', rank: 0 },
  customer: { key: 'customer', label: 'This customer', rank: 1 },
} as const;

const groupOf = (t: EventDatasetTemplate) => (t.owner === 'customer' ? GROUPS.customer : GROUPS.global);

const groupBy = (t: EventDatasetTemplate) => {
  const { key, label } = groupOf(t);
  return [{ key, label }];
};

const listConfig: EntityListItemConfig<EventDatasetTemplate> = {
  getPrimaryText: (t) => t.name,
  getSecondaryText: (t) => t.description ?? '',
  getId: (t) => t.template_id,
  getSearchText: (t) => `${t.name} ${t.description ?? ''} ${(t.tags ?? []).join(' ')}`,
  getTestId: (t) => `dataset-template-row-${t.template_id}`,
};

interface DatasetTemplatesPanelProps {
  customerId: string;
  /** Opens the dataset editor filled in from the definition; nothing is created until it is saved. */
  onUse: (definition: EventDatasetDefinition) => void;
  onBack: () => void;
  notify: (message: string, severity?: 'success' | 'error') => void;
}

const LABEL_WIDTH = 160;

const TemplateDetail: React.FC<{
  template: EventDatasetTemplate;
  canWrite: boolean;
  onUse: () => void;
  onDelete: () => void;
}> = ({ template, canWrite, onUse, onDelete }) => {
  const { definition } = template;
  // One input is shown as before; several each name their position.
  const label = (n: number, what: string) => (definition.inputs.length > 1 ? `${inputLabel(n)} ${what.toLowerCase()}` : what);
  const deletable = canWrite && template.owner === 'customer';
  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }} data-testid="dataset-template-detail">
      <Box sx={{ px: 3, py: 1.5, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="subtitle1" fontWeight={600}>{template.name}</Typography>
        <Chip label={groupOf(template).label} size="small" variant="outlined" />
      </Box>
      <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 2 }}>
        <Box sx={{ maxWidth: 800 }}>
          {template.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{template.description}</Typography>
          )}
          <DetailRow label="Dataset name" value={definition.name} mono labelWidth={LABEL_WIDTH} />
          {definition.inputs.map((input, n) => (
            <DetailRow key={n} label={label(n, 'Event pattern')} value={JSON.stringify(input.event_pattern)} mono labelWidth={LABEL_WIDTH} />
          ))}
          <DetailRow label="One row is" value={definition.row_path || 'One event'} mono labelWidth={LABEL_WIDTH} />
          <DetailRow
            label="Columns"
            value={definition.columns.map((c) => `${c.name} (${c.type}${c.required ? ', required' : ''})`).join(', ')}
            mono
            labelWidth={LABEL_WIDTH}
          />
          {definition.inputs.map((input, n) => (typeof input.mapping.resource === 'string' && (
            <DetailRow key={n} label={label(n, 'Resource path')} value={input.mapping.resource} mono labelWidth={LABEL_WIDTH} />
          )))}
          <DetailRow label="Key fields" value={definition.key_fields.join(', ')} mono labelWidth={LABEL_WIDTH} />
          <DetailRow label="Ordering" value={definition.ordering_field ?? 'event_time'} mono labelWidth={LABEL_WIDTH} />
          <DetailRow label="Current state" value={definition.keep_current_state ? 'Kept per key' : 'History only'} labelWidth={LABEL_WIDTH} />
          <DetailRow label="Retention" value={`${definition.retention_days} days`} labelWidth={LABEL_WIDTH} />
        </Box>
      </Box>
      {canWrite && (
        <FormActionBar
          hideSave
          saving={false}
          size="small"
          onDelete={deletable ? onDelete : undefined}
          sx={{ px: 3, py: 1.5, mt: 0 }}
        >
          <Button size="small" variant="contained" onClick={onUse}>Use as Dataset</Button>
        </FormActionBar>
      )}
    </Box>
  );
};

const DatasetTemplatesPanel: React.FC<DatasetTemplatesPanelProps> = ({ customerId, onUse, onBack, notify }) => {
  const { templates, loading, error } = useEventDatasetTemplates(customerId);
  const canWrite = useDatasetWriteAccess();
  const deletion = useDeleteEventDatasetTemplate(customerId);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // The groups show in a fixed order: the list panel keeps the order it first meets each group in.
  const ordered = useMemo(
    () => [...templates].sort((a, b) => groupOf(a).rank - groupOf(b).rank || a.name.localeCompare(b.name)),
    [templates],
  );
  const selected = templates.find((t) => t.template_id === selectedId) ?? null;

  return (
    <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', minWidth: 0 }} data-testid="dataset-templates-panel">
      <Box sx={{ width: 320, display: 'flex', flexDirection: 'column', borderRight: 1, borderColor: 'divider', bgcolor: 'background.default' }}>
        <EntityListPanel
          entities={ordered}
          selectedEntity={selected}
          isNew={false}
          saving={false}
          config={listConfig}
          emptyStateText={error ? 'Templates could not be read' : 'No dataset templates'}
          emptyStateHelperText=""
          onSelect={(t) => setSelectedId(t.template_id)}
          headerContent={
            <Button fullWidth size="small" variant="outlined" startIcon={<BackIcon />} onClick={onBack}>
              Back to datasets
            </Button>
          }
          isLoading={loading}
          searchable
          groupBy={groupBy}
        />
      </Box>

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
        {error && <InlineAlert severity="error" sx={{ mx: 3, mt: 2 }}>{error}</InlineAlert>}
        {selected ? (
          <TemplateDetail
            key={selected.template_id}
            template={selected}
            canWrite={canWrite}
            onUse={() => onUse(selected.definition)}
            onDelete={() => setConfirmingDelete(true)}
          />
        ) : (
          <EmptyStateMessage
            icon={TemplatesIcon}
            title="Select a template to see its definition"
            description="A template fills in a new dataset; nothing is created until you save it"
          />
        )}
      </Box>

      {selected && (
        <DatasetConfirmDialog
          open={confirmingDelete}
          title="Delete template"
          datasetName={selected.name}
          confirmText="Delete template"
          requireTypedName={false}
          busy={deletion.deleting}
          error={deletion.error}
          onCancel={() => {
            setConfirmingDelete(false);
            deletion.reset();
          }}
          onConfirm={() => deletion.removeTemplate(selected.template_id, {
            onDone: () => {
              setConfirmingDelete(false);
              setSelectedId(null);
              notify('Template deleted');
            },
            onFailed: (message) => notify(message, 'error'),
          })}
        >
          <Typography>
            Delete the template <strong>{selected.name}</strong>? Datasets made from it keep their own definition.
          </Typography>
        </DatasetConfirmDialog>
      )}
    </Box>
  );
};

export default DatasetTemplatesPanel;
